import { minutesToMilliseconds } from 'date-fns';
import { ID } from '@ianesthesie-kit/services';
import { BillingLineDto, MinimalBillingLineDto } from './billingLine.model';
import { lineBalance } from './billingLine.utils';
import {
  DeskPayment,
  DeskPaymentStatus,
  PaymentPath
} from './deskPayment.model';

/**
 * Pure, React-free derivations over a patient's desk payments, shared by the PDV receivables
 * rows and (later) the chase dashboard, following the `billingLine.utils.ts` precedent: a rule
 * is stated once and cannot drift between surfaces. Anything needing hooks or i18n belongs in
 * the components / `deskPayment.labels.ts`.
 */

/**
 * The line fields the derivations below read; both line DTOs structurally satisfy it. The
 * intervention is one of them: a hold taken before the acte line existed is stated on the
 * payment's surgical event alone, and it holds that event's acte line all the same (see
 * {@link findLockingDeskPaymentForLine}).
 */
export type DeskPaymentLineTarget = Pick<
  BillingLineDto,
  'id' | 'lineType' | 'storedStatus' | 'surgicalEventId'
>;

/** Additionally read by {@link findSettleLineForOutcome}, which prices the final capture. */
export type DeskPaymentSettleLineTarget = DeskPaymentLineTarget &
  MinimalBillingLineDto;

/** Waiting on the reader (`CREATED`) or on the capture (`AUTHORIZED`) — the poll keeps running. */
export const isDeskPaymentPending = (status: DeskPaymentStatus): boolean =>
  status === 'CREATED' || status === 'AUTHORIZED';

/**
 * The consultation has been collected, whether or not the intervention is still held on the
 * card. What makes the line PAID server-side, hence what the caches outside the payment
 * itself have to be told about ({@link deskPaymentCaptureTags}).
 */
export const isDeskPaymentCaptured = (status: DeskPaymentStatus): boolean =>
  status === 'PARTIALLY_CAPTURED' || status === 'SETTLED';

/** Declined or called off at the desk: nothing was collected and the line is free again. */
export const isDeskPaymentAbandoned = (status: DeskPaymentStatus): boolean =>
  status === 'FAILED' || status === 'CANCELED';

/** A state no later payment read can advance. */
export const isDeskPaymentTerminal = (status: DeskPaymentStatus): boolean =>
  status === 'SETTLED' ||
  status === 'RELEASED' ||
  isDeskPaymentAbandoned(status);

/**
 * `|| 0`: a payment whose date fails to parse sorts oldest rather than winning a comparator
 * with `NaN`, and reads as long past every deadline seeded from it rather than as live forever.
 */
export const deskPaymentCreatedAt = (payment: DeskPayment): number =>
  Date.parse(payment.createdDate) || 0;

/** Stable identity shared by every row that renews the same first tap. */
export const deskPaymentChainRootId = (payment: DeskPayment): ID =>
  payment.chainRootId ?? payment.paymentIntentId;

export const isSameDeskPaymentChain = (
  left: DeskPayment,
  right: DeskPayment
): boolean => deskPaymentChainRootId(left) === deskPaymentChainRootId(right);

/**
 * Resolves a payment snapshot to the newest row known for its renewal chain. Patient lists
 * expose heads only, while dialogs and the registry may still hold an older id from before a
 * sweeper pass. Renewal count is authoritative; creation time breaks ties for old payloads.
 */
export const findDeskPaymentChainHead = (
  payment: DeskPayment,
  candidates: readonly DeskPayment[] | undefined
): DeskPayment => {
  const chainById = new Map<ID, DeskPayment>([
    [payment.paymentIntentId, payment]
  ]);
  for (const candidate of candidates ?? []) {
    if (isSameDeskPaymentChain(candidate, payment)) {
      // An incoming read is fresher than a snapshot held by a dialog or the registry, even
      // when it describes the same row with the same count and creation date.
      chainById.set(candidate.paymentIntentId, candidate);
    }
  }
  return [...chainById.values()].sort(
    (a, b) =>
      (b.renewalCount ?? 0) - (a.renewalCount ?? 0) ||
      deskPaymentCreatedAt(b) - deskPaymentCreatedAt(a)
  )[0];
};

/**
 * Chain changes whose renewal work can update billing data outside the payment itself: replacing
 * an old intent writes authorization/history, clearing its queued outcome may flag the acte for
 * attention, and a terminal status can settle, waive or flag the line. Repeated equal reads are
 * deliberately silent so invalidating the payment list cannot loop.
 */
export const findDeskPaymentBillingTransitions = (
  previous: readonly DeskPayment[] | undefined,
  current: readonly DeskPayment[] | undefined
): DeskPayment[] =>
  (current ?? []).filter((payment) => {
    const prior = (previous ?? [])
      .filter((candidate) => isSameDeskPaymentChain(candidate, payment))
      .sort(
        (a, b) =>
          (b.renewalCount ?? 0) - (a.renewalCount ?? 0) ||
          deskPaymentCreatedAt(b) - deskPaymentCreatedAt(a)
      )[0];
    return (
      !!prior &&
      (prior.paymentIntentId !== payment.paymentIntentId ||
        (isDeskPaymentTerminal(payment.status) &&
          prior.status !== payment.status) ||
        (prior.outcomePending ?? false) !== (payment.outcomePending ?? false))
    );
  });

/**
 * How long a pending payment is worth re-reading for. The backend sweeper abandons a CREATED
 * row after 10 minutes but never touches an AUTHORIZED one, so without an upper bound a
 * payment left hanging on a reader would keep every open patient file polling for the whole
 * shift. 20 minutes clears the sweeper's own window; past it the row is still reachable
 * through its "resume" button, whose tracker re-reads and heals it.
 */
export const DESK_PAYMENT_PENDING_HORIZON_MS = minutesToMilliseconds(20);

/**
 * Whether a payment is pending *and* recent enough to still be worth working on — the gate on
 * both re-reading the patient's list and keeping a `POST /refresh` running behind a closed
 * dialog. Bounded by {@link DESK_PAYMENT_PENDING_HORIZON_MS}, since a pending row is not proof
 * anything is still happening.
 */
export const isLiveDeskPaymentPending = (
  payment: DeskPayment,
  now: number
): boolean =>
  isDeskPaymentPending(payment.status) &&
  // One-sided on purpose: a desk clock behind the server reads the payment as younger than it
  // is, stretching the window by the skew but never past it, since `now` keeps advancing. An
  // absolute value would instead fail closed — a clock 25 minutes late would treat a payment
  // created a second ago as long past the horizon, and nothing would poll it.
  now - deskPaymentCreatedAt(payment) < DESK_PAYMENT_PENDING_HORIZON_MS;

/**
 * Whether a patient's list holds such a payment: the GET list is a plain DB read, so re-reading
 * it is how a capture obtained elsewhere — another desk, another tab, or a webhook landing after
 * the list last loaded — is ever noticed on this surface.
 */
export const hasLivePendingDeskPayment = (
  payments: DeskPayment[] | undefined,
  now: number
): boolean =>
  (payments ?? []).some((payment) => isLiveDeskPaymentPending(payment, now));

/**
 * Whether the patient list must keep re-reading. A renewal gap is not a reader payment and
 * must never be sent to `/refresh`; polling the list is how its next chain head replaces it.
 */
export const hasActiveDeskPaymentChain = (
  payments: DeskPayment[] | undefined,
  now: number
): boolean =>
  (payments ?? []).some(
    (payment) =>
      payment.status === 'RENEWING' || isLiveDeskPaymentPending(payment, now)
  );

/**
 * Takes a payment into the registry held by `deskPaymentRegistry.slice.ts`, which states what
 * that registry is for, and drops the entries that have since gone past their horizon. Keyed by
 * intent, so a payment reported twice — resumed, then reported again by a row — is followed
 * once: `/refresh` is a state transition, not a read.
 */
export const addToDeskPaymentRegistry = (
  registry: readonly DeskPayment[],
  payment: DeskPayment,
  now: number
): DeskPayment[] => [
  ...registry.filter(
    (entry) =>
      !isSameDeskPaymentChain(entry, payment) &&
      isLiveDeskPaymentPending(entry, now)
  ),
  ...(isLiveDeskPaymentPending(payment, now) ? [payment] : [])
];

/**
 * The same registry, told what other reads have since seen of its payments — a row carrying the
 * outcome a colleague's capture produced, say. Only the payments already in it are updated:
 * adopting every payment a read happens to mention is what would put this browser in charge of
 * another desk's work. Returns the registry itself when nothing moved, so a caller storing it in
 * state does not re-render on every read.
 */
export const refreshDeskPaymentRegistry = (
  registry: DeskPayment[],
  seen: (DeskPayment | null | undefined)[],
  now: number
): DeskPayment[] => {
  const latest = seen.filter((payment): payment is DeskPayment => !!payment);
  const next = registry
    // A patient-list read returns chain heads only. Replace an older registered id with that
    // head before applying the pending horizon, so a cache entry from before a renewal cannot
    // keep following an intent the chain has already replaced.
    .map((entry) => findDeskPaymentChainHead(entry, latest))
    .filter((entry) => isLiveDeskPaymentPending(entry, now));
  return next.length === registry.length &&
    next.every((entry, index) => entry === registry[index])
    ? registry
    : next;
};

/**
 * The statuses that hold a line server-side (medical-synthesis' `LINE_OCCUPYING_STATUSES`):
 * they block a second desk payment on it and make the backend refuse the V0 mutations that
 * would move its amounts — record a payment, override the total, waive, mark paid externally
 * — with `E1-12-2-1072`.
 */
export const occupiesLine = (status: DeskPaymentStatus): boolean =>
  status === 'CREATED' ||
  status === 'AUTHORIZED' ||
  status === 'PARTIALLY_CAPTURED' ||
  status === 'RENEWING';

/**
 * A payment taken on an acte line itself — the intervention collected at the desk after
 * surgery, in one capture, with nothing held afterwards. Recognised on the absent consultation
 * line rather than on `path === 'ACTE_ONLY'`: the line a payment pays is fixed when it is
 * created, while a path is only certain once the card has been read, and the rules below have
 * to hold from the moment the tap is sent to the reader.
 */
export const isActeOnlyDeskPayment = (payment: DeskPayment): boolean =>
  !payment.consultationLineId && payment.path !== 'RENEWED_HOLD';

/** The two paths whose intervention share is authorized for later capture. */
export const isHoldPaymentPath = (
  path: PaymentPath | null
): path is 'TWO_BITE' | 'RENEWED_HOLD' =>
  path === 'TWO_BITE' || path === 'RENEWED_HOLD';

/**
 * Every line this payment has already captured on, and which a capture therefore turns PAID
 * server-side: the consultation it collected, the acte itself when the acte is what was paid,
 * and — once a two-bite payment actually captured an amount — the acte line its final capture
 * landed on as well. That last one is the whole point: a hold settled at another desk is a
 * capture no surface here witnessed, and asking only about the consultation would find it long
 * PAID and conclude there was nothing to reconcile.
 *
 * A `PARTIALLY_CAPTURED` first tap names the consultation alone; a renewed hold names no
 * captured line. In both cases the acte amount is held rather than paid.
 */
export const deskPaymentCapturedLineIds = (payment: DeskPayment): ID[] => {
  const { consultationLineId, acteLineId } = payment;
  // An acte-only payment collects the acte itself, in one capture.
  if (isActeOnlyDeskPayment(payment)) {
    return acteLineId ? [acteLineId] : [];
  }
  const capturedConsultation = consultationLineId ? [consultationLineId] : [];
  return payment.status === 'SETTLED' &&
    isHoldPaymentPath(payment.path) &&
    payment.amountCapturedOnActe > 0 &&
    acteLineId
    ? [...capturedConsultation, acteLineId]
    : capturedConsultation;
};

/**
 * An acte line carries the held intervention amount, a consultation line the captured one. A
 * renewed hold names neither while no acte exists, so its surgery's consultation remains the
 * stand-in that shows the hold and its outcome control until the acte line is attached.
 */
const matchesLine = (payment: DeskPayment, line: DeskPaymentLineTarget) => {
  if (line.lineType === 'ACTE') {
    return payment.acteLineId === line.id;
  }
  return (
    payment.consultationLineId === line.id ||
    (payment.path === 'RENEWED_HOLD' &&
      payment.consultationLineId === null &&
      payment.acteLineId === null &&
      payment.surgicalEventId === line.surgicalEventId)
  );
};

/**
 * The one payment a chase-dashboard row carries, as the list every derivation below takes.
 * `undefined` on a line with none: the derivations read it as an empty list, which is the same
 * answer here — unlike the patient file, where an absent list also covers "not loaded yet".
 */
export const deskPaymentsOfLine = (
  payment: DeskPayment | null | undefined
): DeskPayment[] | undefined => (payment ? [payment] : undefined);

/**
 * The line's current desk payment: the newest attempt that still says something about the
 * line's state, so a declined or desk-canceled one never masks the live payment that followed
 * it. The BFF already returns newest first; sorted here anyway so the rules below do not
 * depend on the response order.
 */
export const findLatestDeskPaymentForLine = (
  payments: DeskPayment[] | undefined,
  line: DeskPaymentLineTarget
): DeskPayment | undefined =>
  (payments ?? [])
    .filter(
      (payment) =>
        matchesLine(payment, line) &&
        payment.status !== 'FAILED' &&
        payment.status !== 'CANCELED'
    )
    .sort((a, b) => {
      const createdOrder = deskPaymentCreatedAt(b) - deskPaymentCreatedAt(a);
      return isSameDeskPaymentChain(a, b)
        ? (b.renewalCount ?? 0) - (a.renewalCount ?? 0) || createdOrder
        : createdOrder;
    })[0];

/**
 * The payment currently live on a reader for this line, if any — the one a reopened dialog
 * resumes following. Not the gate on starting a new payment: that is the wider
 * {@link findLockingDeskPaymentForLine}, since a hold occupies the line without being pending.
 *
 * An acte line only ever resumes a payment of its own: a two-bite tap on the consultation is
 * live on that row, and the acte's part of it is a hold to be resolved after surgery, not a
 * payment to walk back to the reader for.
 */
export const findPendingDeskPaymentForLine = (
  payments: DeskPayment[] | undefined,
  line: DeskPaymentLineTarget
): DeskPayment | undefined => {
  const payment = findLatestDeskPaymentForLine(payments, line);
  if (!payment || !isDeskPaymentPending(payment.status)) {
    return undefined;
  }
  return line.lineType !== 'ACTE' || isActeOnlyDeskPayment(payment)
    ? payment
    : undefined;
};

/**
 * A two-bite hold with money still on the card: authorized, and not yet captured, given back or
 * written off. `SINGLE_BITE` never holds anything, and a RELEASED hold no longer does.
 */
export const isLiveStripeHold = (payment: DeskPayment): boolean =>
  isHoldPaymentPath(payment.path) &&
  (payment.status === 'AUTHORIZED' || payment.status === 'PARTIALLY_CAPTURED');

/** The chain still reserves the intervention while its next hold waits to be authorized. */
const isRenewingHold = (payment: DeskPayment) =>
  isHoldPaymentPath(payment.path) && payment.status === 'RENEWING';

const reservesIntervention = (payment: DeskPayment) =>
  isLiveStripeHold(payment) || isRenewingHold(payment);

/**
 * A hold chain the intervention outcome can still act on. `PARTIALLY_CAPTURED` acts on the
 * current authorization; `RENEWING` records the outcome for the replacement to apply. An
 * AUTHORIZED payment has not reached either state yet, and terminal rows have nothing left to
 * capture or give back.
 */
export const isResolvableHold = (payment: DeskPayment): boolean =>
  isHoldPaymentPath(payment.path) &&
  (payment.status === 'PARTIALLY_CAPTURED' || payment.status === 'RENEWING');

/**
 * A live hold taken before the acte line existed — priced off the CCAM grid, so no line was
 * there to attach it to. It holds that intervention's acte line all the same: medical-synthesis
 * resolves the line at the outcome and refuses meanwhile anything that would move its amounts,
 * the hold being what is meant to settle it.
 */
const holdsUnlinkedActe = (payment: DeskPayment, line: DeskPaymentLineTarget) =>
  payment.acteLineId === null &&
  payment.amountIntervention > 0 &&
  payment.surgicalEventId === line.surgicalEventId &&
  reservesIntervention(payment);

/**
 * Whether this payment holds that line, the frontend's copy of the backend's own lock. What
 * locks a line depends on what the payment does to it: the line it collects — a consultation,
 * or the acte of an acte-only payment — is held by any payment {@link occupiesLine occupying}
 * it, while an acte covered by a hold is held only while that hold {@link isLiveHold is live},
 * its still-uncaptured amount being the acte's own (`E1-12-2-1078` is the backend refusing a
 * second payment on a line in that state).
 */
const locksLine = (payment: DeskPayment, line: DeskPaymentLineTarget) => {
  if (line.lineType !== 'ACTE') {
    return (
      payment.consultationLineId === line.id && occupiesLine(payment.status)
    );
  }
  if (payment.acteLineId !== line.id) {
    return holdsUnlinkedActe(payment, line);
  }
  return isActeOnlyDeskPayment(payment)
    ? occupiesLine(payment.status)
    : reservesIntervention(payment);
};

/**
 * The desk payment {@link locksLine locking} this line, if any, so an action the backend would
 * refuse with `E1-12-2-1072` is never offered.
 *
 * Deliberately not `findLatestDeskPaymentForLine`: the lock is the backend's, and the backend
 * asks whether *any* such payment exists rather than what the newest one says.
 */
export const findLockingDeskPaymentForLine = (
  payments: DeskPayment[] | undefined,
  line: DeskPaymentLineTarget
): DeskPayment | undefined =>
  (payments ?? []).find((payment) => locksLine(payment, line));

/**
 * The renewal the platform means to attempt on a live hold, for the pill to word. Both values are
 * the backend's, and both may be missing: the surgery has no date yet, or the payload predates the
 * field. A surface quoting either has to drop its clause rather than show a placeholder, since
 * this states an intent to try and must not read as a promise the money will be there.
 */
export type DeskPaymentRenewalPlan = {
  /** ISO date (`yyyy-MM-dd`) of the surgery the renewals aim to cover. */
  targetDate: string | null;
  /** ISO instant at or after which no renewal is attempted. */
  limit: string | null;
};

/**
 * What a line's payment-state pill has to say, wording and formatting left to the pill itself:
 * - `inProgress` — the line is locked by a payment still on its way: the reader is waiting for
 *   the tap (consultation line), or the hold the line will carry is being authorized (acte);
 * - `held` — the hold on the intervention amount, with the amount and the deadline funding it,
 *   on whichever line {@link rendersDeskPaymentHold} designates, plus the renewal the platform
 *   means to attempt on it when there is one ({@link DeskPaymentRenewalPlan});
 * - `holdExpired` — that same hold past its deadline: the funds are no longer capturable, and
 *   the outcome control next to the pill is what frees the line, by releasing the dead hold;
 * - `released` — that hold was given back to the patient instead of being captured: the
 *   intervention was canceled, nothing was left to collect on the acte line, or it ran out on
 *   Stripe's side and the sweeper closed it — the payment itself no longer says which, only its
 *   trail (`HoldReleaseReason`) does, so the pill words all three alike;
 * - `settled` — the hold was captured, and the acte line is still OPEN: the final capture was
 *   smaller than what the line owes, so a remainder stays to be collected. Only an OPEN acte
 *   line says it — once the line is PAID its own status chip carries the outcome;
 * - `noHold` — no hold was possible (single-bite card): the intervention has to be collected
 *   after surgery. Reported on the acte line it concerns, or on the consultation line while
 *   no acte line is linked to the payment;
 * - `lockedByHold` — the acte line is held by a hold that names no line yet, so the hold
 *   itself is stated on the consultation stand-in ({@link rendersInterventionState}). Said
 *   without the amount or the deadline on purpose: this row explains why its own actions are
 *   barred, it does not report the hold a second time.
 */
export type DeskPaymentPillState =
  | { kind: 'inProgress' }
  | {
      kind: 'held';
      amount: number;
      captureBefore: string | null;
      /** Present only while a renewal is planned; see {@link DeskPaymentRenewalPlan}. */
      renewal?: DeskPaymentRenewalPlan;
    }
  | { kind: 'renewing' }
  | { kind: 'holdExpired'; amount: number; captureBefore: string | null }
  | { kind: 'released' }
  | { kind: 'settled' }
  | { kind: 'noHold' }
  | { kind: 'lockedByHold' };

/**
 * A payment that collected the consultation while leaving an intervention amount uncollected
 * and unheld. A SETTLED two-bite payment is a hold that *was* collected, not a card that could
 * not hold: only the paths that never held anything owe the row this warning.
 */
const isNoHoldSettlement = (payment: DeskPayment) =>
  payment.status === 'SETTLED' &&
  !isHoldPaymentPath(payment.path) &&
  // An acte-only payment *is* the intervention being collected, so it owes no such warning.
  !isActeOnlyDeskPayment(payment) &&
  payment.amountIntervention > 0;

/**
 * Which line reports what the intervention amount still owes the desk — the hold on it, or the
 * warning that nothing could be held. It belongs to the acte line, but that line may not exist
 * yet, or not be linked to the payment (an amount estimated off the CCAM grid is the common
 * case), and a hold with no row to appear on would leave the desk with no sign of the money on
 * the card and no deadline to collect it by. So while `acteLineId` is unset the consultation
 * line stands in; once it is set, the acte line alone reports it and the pill is never doubled.
 */
const rendersInterventionState = (
  payment: DeskPayment,
  line: DeskPaymentLineTarget
) => line.lineType === 'ACTE' || !payment.acteLineId;

/**
 * Whether this line is the one to show the payment's hold. Recognised on the status alone, not
 * on `path`: `PARTIALLY_CAPTURED` means funds are still held on the card whichever path got
 * them there.
 */
export const rendersDeskPaymentHold = (
  payment: DeskPayment,
  line: DeskPaymentLineTarget
): boolean =>
  (payment.status === 'PARTIALLY_CAPTURED' || payment.status === 'RENEWING') &&
  // An acte-only payment holds nothing: it captures the acte's balance in one go, so an
  // intermediate state of its own is a passing server-side step, not money on the card.
  !isActeOnlyDeskPayment(payment) &&
  matchesLine(payment, line) &&
  rendersInterventionState(payment, line);

/**
 * Whether the hold has outlived its deadline, and a capture on it would be refused
 * (`E1-12-2-1075`). Compared against the caller's clock, since the backend only closes the hold
 * out — RELEASED, `HOLD_EXPIRED` in the trail — once the sweeper's next pass or a webhook
 * catches it, until when the row would keep promising a hold that can no longer be captured.
 * Nothing polls in this state (`PARTIALLY_CAPTURED` is not pending), so the caller has to advance
 * that clock on its own.
 */
export const isHoldExpired = (payment: DeskPayment, now: number): boolean =>
  payment.status !== 'RENEWING' &&
  !!payment.captureBefore &&
  Date.parse(payment.captureBefore) <= now;

/**
 * The renewal a live hold carries, read off `renewalPlanned` alone: whether this card can be
 * charged again, whether its window already reaches the surgery and whether the chain has room
 * left are all server-side facts, so inferring one from the path or the dates here would promise
 * renewals the backend will never attempt.
 */
const deriveRenewalPlan = (
  payment: DeskPayment
): DeskPaymentRenewalPlan | undefined =>
  payment.renewalPlanned
    ? {
        targetDate: payment.renewalTargetDate ?? null,
        limit: payment.renewalLimit ?? null
      }
    : undefined;

const deriveHoldPill = (
  payment: DeskPayment,
  now: number
): DeskPaymentPillState => {
  if (payment.status === 'RENEWING') {
    return { kind: 'renewing' };
  }
  const hold = {
    amount: payment.amountIntervention,
    captureBefore: payment.captureBefore
  };
  // A hold past its deadline says nothing about renewals: the funds are gone either way, and the
  // backend's flag was derived before the deadline it outlived.
  return isHoldExpired(payment, now)
    ? { kind: 'holdExpired', ...hold }
    : { kind: 'held', ...hold, renewal: deriveRenewalPlan(payment) };
};

/**
 * A two-bite hold that ended without being captured — given back at the outcome, or run out on
 * Stripe's side and closed by the sweeper — reported on the line that carried it, so the row
 * explains why money the patient saw blocked never arrived. Both endings are RELEASED; only the
 * trail's `HoldReleaseReason` tells them apart, and the pill words them the same either way.
 */
const rendersEndedHold = (payment: DeskPayment, line: DeskPaymentLineTarget) =>
  payment.status === 'RELEASED' &&
  isHoldPaymentPath(payment.path) &&
  matchesLine(payment, line) &&
  rendersInterventionState(payment, line);

/**
 * The same ending, reached with a SETTLED row: a zero-amount final capture can finish the Stripe
 * intent as `succeeded`. The intent's captured amount distinguishes that from a real acte
 * capture even when the line carries unrelated payment rows. Checked before
 * {@link rendersSettledRemainder}, which would otherwise claim the hold was collected.
 */
const rendersReleasedSettledHold = (
  payment: DeskPayment,
  line: DeskPaymentLineTarget
) =>
  payment.status === 'SETTLED' &&
  isHoldPaymentPath(payment.path) &&
  line.lineType === 'ACTE' &&
  matchesLine(payment, line) &&
  payment.amountCapturedOnActe <= 0;

/**
 * A two-bite hold captured on an acte line that is still OPEN: the desk settled the
 * intervention for less than the line owes — the hold could not cover it — and the remainder
 * has to be collected another way. A PAID line says the opposite through its own status chip,
 * and only the acte line knows the remainder, so the consultation stand-in never claims one.
 */
const rendersSettledRemainder = (
  payment: DeskPayment,
  line: DeskPaymentLineTarget
) =>
  payment.status === 'SETTLED' &&
  isHoldPaymentPath(payment.path) &&
  payment.amountCapturedOnActe > 0 &&
  line.lineType === 'ACTE' &&
  line.storedStatus === 'OPEN' &&
  matchesLine(payment, line);

/**
 * What an acte line has to say about a payment that names no line of its own: an intervention
 * amount priced off the CCAM grid is held before any acte line exists to attach it to, so
 * {@link findLatestDeskPaymentForLine} — which matches on the line ids — sees nothing, while
 * the backend holds the line all the same ({@link holdsUnlinkedActe}). Without this the row
 * would offer no explanation at all for the actions its lock has just killed.
 *
 * Reached only from an acte line: on any other line type {@link locksLine} requires the very id
 * match the newest-payment rule already makes, so a miss there is a genuine "nothing to say".
 * The hold can only be live here, hence the two states — the tap being authorized, and the hold
 * itself, which the consultation stand-in words in full.
 */
const deriveUnlinkedHoldPill = (
  payments: DeskPayment[] | undefined,
  line: DeskPaymentLineTarget
): DeskPaymentPillState | undefined => {
  const lock = findLockingDeskPaymentForLine(payments, line);
  if (!lock) {
    return undefined;
  }
  return isDeskPaymentPending(lock.status)
    ? { kind: 'inProgress' }
    : { kind: 'lockedByHold' };
};

/**
 * Everything the payments themselves can say, in precedence order — most immediate first: what
 * is still on the card (a hold, a tap on its way), then what became of it (released, settled
 * short), then the standing warning that nothing could be held at all.
 */
const derivePillFromPayments = (
  line: DeskPaymentLineTarget,
  payments: DeskPayment[] | undefined,
  now: number
): DeskPaymentPillState | undefined => {
  const payment = findLatestDeskPaymentForLine(payments, line);
  if (!payment) {
    return deriveUnlinkedHoldPill(payments, line);
  }
  if (rendersDeskPaymentHold(payment, line)) {
    return deriveHoldPill(payment, now);
  }
  // A pending payment the line is locked by, so the actions it disables always come with their
  // explanation: the line waiting for the tap — a consultation, or an acte collected on its
  // own — and the acte whose two-bite hold is being authorized on the consultation's tap.
  if (isDeskPaymentPending(payment.status) && locksLine(payment, line)) {
    return { kind: 'inProgress' };
  }
  if (
    rendersEndedHold(payment, line) ||
    rendersReleasedSettledHold(payment, line)
  ) {
    return { kind: 'released' };
  }
  if (rendersSettledRemainder(payment, line)) {
    return { kind: 'settled' };
  }
  return isNoHoldSettlement(payment) && owesInterventionWarning(payment, line)
    ? { kind: 'noHold' }
    : undefined;
};

/**
 * The card-payment state a billing line shows next to its own status, or `undefined` when
 * there is nothing to add — in particular once a consultation is captured with nothing left
 * to warn about: the line is then PAID and its status chip carries that. The single place the
 * wording is decided, so no two surfaces can word the same instant differently.
 */
export const deriveDeskPaymentPill = (
  line: DeskPaymentLineTarget,
  payments: DeskPayment[] | undefined,
  /** The clock the hold deadline is compared against; the caller owns when it advances. */
  now: number,
  /**
   * The backend's own lock, for a surface whose payments cannot carry the hold at all: the
   * chase dashboard's row is given the one payment that *names* the line, selected server-side,
   * so an acte held by a hold priced off the CCAM grid arrives with none — while the lock on it
   * does, as `lockedByDeskPayment`. Without it the row would show the tooltip its lock produces
   * and no pill beside it.
   */
  lockedWithoutPayment = false
): DeskPaymentPillState | undefined =>
  derivePillFromPayments(line, payments, now) ??
  // Only once the payments have yielded nothing, and only on an acte: on any other line type
  // the locking payment names the line, so the surface would have carried it. Worded
  // `lockedByHold` rather than the `inProgress` its list-based twin reports for a pending lock:
  // the flag alone cannot tell an AUTHORIZED tap from the PARTIALLY_CAPTURED hold, and the
  // backend sweeper never voids an AUTHORIZED row — a stranded one keeps that state — so the
  // hold is the state that lasts and the honest default. Wording the two apart needs the
  // locking payment on the row DTO (deferred to 3c).
  (lockedWithoutPayment && line.lineType === 'ACTE'
    ? { kind: 'lockedByHold' }
    : undefined);

/**
 * The `noHold` warning says the intervention still has to be collected: an acte line that has
 * since been settled or waived owes nothing, so it drops the warning rather than contradicting
 * its own status chip. The consultation stand-in keeps it whatever its status — it is PAID by
 * the very capture that could not hold, and the warning is then the only trace of the money
 * still owed until an acte line exists to carry it.
 */
const owesInterventionWarning = (
  payment: DeskPayment,
  line: DeskPaymentLineTarget
) =>
  rendersInterventionState(payment, line) &&
  (line.lineType !== 'ACTE' || line.storedStatus === 'OPEN');

/**
 * The hold this line has to answer for, if any: the payment behind its held, renewing or expired
 * pill, and the chain head the intervention-outcome control resolves. Read off the same
 * newest-payment rule as the pill, so the control appears exactly where the reservation is
 * stated, including the short renewal gap.
 */
export const findHoldToResolveForLine = (
  payments: DeskPayment[] | undefined,
  line: DeskPaymentLineTarget
): DeskPayment | undefined => {
  const payment = findLatestDeskPaymentForLine(payments, line);
  return payment &&
    isResolvableHold(payment) &&
    rendersDeskPaymentHold(payment, line)
    ? payment
    : undefined;
};

/**
 * The acte line a hold's final capture settles against, read off the payment itself rather
 * than off the row the outcome was opened from: the line the payment already names, and
 * otherwise the acte line of the intervention it was taken for — the one medical-synthesis
 * resolves at the outcome (`resolveActeLineAtOutcome`, attached only while it still owes
 * something). A hold priced off the
 * CCAM grid stays unlinked whenever the devis came out below the estimate, which is the
 * ordinary case, so the unlinked branch is not an edge one.
 *
 * The line's status and balance are deliberately not looked at. The backend caps the capture
 * at the line's balance and releases the hold when nothing is left of it — a re-priced,
 * already-paid or exempted acte — so a settled line is an outcome to price and explain, not a
 * missing one: dropping it here would put the dialog back on "no acte line yet" and lose the
 * amount it has to state.
 *
 * `null` only when the intervention has no acte line at all, which is the one case the backend
 * still refuses (`E1-12-2-1076`).
 */
/**
 * Whether an acte line may be collected at the desk on its own: only once its consultation is
 * settled. While the consultation is owed, its own card payment collects both (the acte balance is
 * its intervention estimate), and offering the acte beside it would let the desk take the
 * intervention and forget the visit — the backend refuses such a start (`CONSULTATION_STILL_OPEN`).
 */
export const isActeCollectableAlone = <T extends DeskPaymentSettleLineTarget>(
  acteLine: T,
  lines: T[] | undefined
): boolean =>
  !(lines ?? []).some(
    (candidate) =>
      candidate.lineType === 'CONSULTATION' &&
      candidate.surgicalEventId === acteLine.surgicalEventId &&
      candidate.storedStatus === 'OPEN' &&
      lineBalance(candidate) > 0
  );

export const findSettleLineForOutcome = <T extends DeskPaymentSettleLineTarget>(
  payment: DeskPayment,
  lines: T[] | undefined
): T | null => {
  const isSettleLine = payment.acteLineId
    ? (candidate: T) => candidate.id === payment.acteLineId
    : (candidate: T) =>
        candidate.lineType === 'ACTE' &&
        candidate.surgicalEventId === payment.surgicalEventId;
  return (lines ?? []).find(isSettleLine) ?? null;
};
